// Import Dependencies
import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";

// Local Imports
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";

// ----------------------------------------------------------------------

const CustomListbox = forwardRef(
  (
    {
      data,
      placeholder,
      label,
      displayField = "label",
      idField, // Added idField
      error,
      multiple,
      inputProps,
      rootProps,
      className,
      classNames,
      value,
      onChange,
      ...rest
    },
    ref,
  ) => {
    
    const options = useMemo(() => {
        return (data || []).map((item, index) => {
           let val;
           if (typeof item === 'object' && item !== null) {
              if (idField && item[idField] !== undefined) {
                  val = item[idField];
              } else {
                  val = item.id !== undefined ? item.id : (item.value !== undefined ? item.value : String(index));
              }
           } else {
              val = item;
           }
           return {
              label: typeof item === 'object' && item !== null ? item[displayField] : item,
              value: val,
              originalItem: item
           }
        });
    }, [data, displayField, idField]);

    const mappedValue = useMemo(() => {
       if (multiple) {
          if (!Array.isArray(value)) return [];
          return value.map(v => {
             const found = options.find(o => 
                 o.originalItem === v || 
                 (typeof v === 'object' && v !== null && (v.id === o.value || v.value === o.value || (idField && v[idField] === o.value))) || 
                 v === o.value
             );
             return found ? found.value : v;
          });
       } else {
          if (value === undefined || value === null) return "";
          const found = options.find(o => 
              o.originalItem === value || 
              (typeof value === 'object' && value !== null && (value.id === o.value || value.value === o.value || (idField && value[idField] === o.value))) || 
              value === o.value
          );
          return found ? found.value : (typeof value === 'object' && value !== null ? (idField && value[idField] !== undefined ? value[idField] : (value.id || value.value || "")) : value);
       }
    }, [value, multiple, options, idField]);

    const handleChange = (newVal) => {
       if (!onChange) return;
       if (multiple) {
           const newItems = newVal.map(v => {
               const found = options.find(o => o.value === v);
               return found ? found.originalItem : v;
           });
           onChange(newItems);
       } else {
           const found = options.find(o => o.value === newVal);
           onChange(found ? found.originalItem : newVal);
       }
    };

    return (
        <div className={className} {...rootProps} ref={ref}>
            <MultiSelectCheckboxDropdown
                options={options}
                value={mappedValue}
                onChange={handleChange}
                label={label}
                placeholder={placeholder || inputProps?.placeholder}
                error={error}
                isMulti={!!multiple}
                {...rest}
            />
        </div>
    );
  },
);

CustomListbox.displayName = "Listbox";

CustomListbox.propTypes = {
  data: PropTypes.array,
  placeholder: PropTypes.node,
  label: PropTypes.node,
  displayField: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  multiple: PropTypes.bool,
  inputProps: PropTypes.object,
  rootProps: PropTypes.object,
  classNames: PropTypes.object,
  className: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func
};

export { CustomListbox as Listbox };
